/**
 * S-REF-FEAT — Référentiel Fonctionnalités applicatives (Admin / Manager) — US-APP-FEAT
 * Hiérarchie N1 (Module) → N2 (Sous-module) → N3 (Feature / code unique)
 * Layout A : volet gauche (applications + modules) / volet droit (table des features).
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  applicationFeaturesApi,
  applicationApi,
  ApiRequestError,
  type ApplicationFeatureDto,
  type ApplicationDto,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";
import { localeLabel } from "../utils/locale";
import { useAuth } from "../context/AuthContext";

const emptyForm = {
  applicationId: "",
  moduleCode: "",
  moduleLabel: "",
  subModuleCode: "",
  subModuleLabel: "",
  code: "",
  labelEn: "",
  labelFr: "",
  description: "",
  active: true,
  sortOrder: 0,
};

type ModuleEntry = { code: string | null; label: string | null; count: number };

/** Modules (N1) distincts des features chargées, avec leur nombre de features. */
function listModules(features: ApplicationFeatureDto[]): ModuleEntry[] {
  const modules: ModuleEntry[] = [];
  for (const f of features) {
    const key = f.moduleCode ?? "";
    const found = modules.find((m) => (m.code ?? "") === key);
    if (found) {
      found.count += 1;
      continue;
    }
    modules.push({ code: f.moduleCode, label: f.moduleLabel, count: 1 });
  }
  return modules;
}

export function ApplicationFeaturesPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  // Référentiel global (pas rattaché à un domaine) : création/édition/import réservés à
  // l'Admin depuis la remédiation 2026-10-02 (cf. docs/reviews/2026-10-02-full-audit.md,
  // décision produit "admin_only") — le backend applique déjà cette restriction
  // (requireRole("admin")) ; ce flag évite d'afficher des contrôles qui échoueraient en 403.
  const isAdmin = (user?.accessLevel ?? 0) >= 100;

  const [applications, setApplications] = useState<ApplicationDto[]>([]);
  const [filterAppId, setFilterAppId] = useState("");
  const [selectedModCode, setSelectedModCode] = useState<string | null>(null);
  const [features, setFeatures] = useState<ApplicationFeatureDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importReport, setImportReport] = useState<{
    created: number;
    updated: number;
    errors: Array<{ row: number; message: string }>;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  async function loadApplications() {
    try {
      const res = await applicationApi.list();
      const activeOnly = res.applications.filter((a) => a.active);
      setApplications(activeOnly);
      setFilterAppId((prev) => (prev && !activeOnly.some((a) => a.id === prev) ? "" : prev));
    } catch { /* silent */ }
  }

  async function loadFeatures(appId = filterAppId) {
    setLoading(true);
    setError(null);
    setFeatures([]);
    try {
      const res = await applicationFeaturesApi.list(appId ? { applicationId: appId } : undefined);
      setFeatures(res.features);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadApplications(); }, []);

  useEffect(() => {
    void loadFeatures(filterAppId);
    setSelectedModCode(null);
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAppId]);

  async function downloadTemplate() {
    try {
      const blob = await applicationFeaturesApi.downloadTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "skillmap-application-features-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("download_failed"));
    }
  }

  async function onImport(event: FormEvent) {
    event.preventDefault();
    if (!importFile) { setError(t("choose_xlsx")); return; }
    setBusy(true);
    setError(null);
    setImportReport(null);
    try {
      const report = await applicationFeaturesApi.importBulk(importFile);
      setImportReport(report);
      setImportFile(null);
      await loadFeatures();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("import_failed"));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(feat: ApplicationFeatureDto) {
    setEditingId(feat.id);
    setShowForm(true);
    setError(null);
    setForm({
      applicationId: feat.applicationId,
      moduleCode: feat.moduleCode ?? "",
      moduleLabel: feat.moduleLabel ?? "",
      subModuleCode: feat.subModuleCode ?? "",
      subModuleLabel: feat.subModuleLabel ?? "",
      code: feat.code,
      labelEn: feat.labelEn,
      labelFr: feat.labelFr ?? "",
      description: feat.description ?? "",
      active: feat.active,
      sortOrder: feat.sortOrder,
    });
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(false);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      if (editingId) {
        await applicationFeaturesApi.patch(editingId, {
          labelEn: form.labelEn.trim(),
          labelFr: form.labelFr.trim() || undefined,
          description: form.description.trim() || undefined,
          moduleCode: form.moduleCode.trim().toUpperCase() || undefined,
          moduleLabel: form.moduleLabel.trim() || undefined,
          subModuleCode: form.subModuleCode.trim().toUpperCase() || undefined,
          subModuleLabel: form.subModuleLabel.trim() || undefined,
          active: form.active,
          sortOrder: form.sortOrder,
        });
        setEditingId(null);
      } else {
        if (!form.applicationId) { setError(t("feature_application_required")); return; }
        await applicationFeaturesApi.create({
          applicationId: form.applicationId,
          code: form.code.trim().toUpperCase(),
          labelEn: form.labelEn.trim(),
          labelFr: form.labelFr.trim() || undefined,
          description: form.description.trim() || undefined,
          moduleCode: form.moduleCode.trim().toUpperCase() || undefined,
          moduleLabel: form.moduleLabel.trim() || undefined,
          subModuleCode: form.subModuleCode.trim().toUpperCase() || undefined,
          subModuleLabel: form.subModuleLabel.trim() || undefined,
          active: form.active,
          sortOrder: form.sortOrder,
        });
      }
      setForm(emptyForm);
      setShowForm(false);
      await loadFeatures();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("save_failed"));
    }
  }

  async function toggleActive(feat: ApplicationFeatureDto) {
    setError(null);
    try {
      await applicationFeaturesApi.patch(feat.id, { active: !feat.active });
      await loadFeatures();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("update_failed"));
    }
  }

  const modules = useMemo(() => listModules(features), [features]);
  const visibleFeatures = useMemo(
    () =>
      selectedModCode === null
        ? features
        : features.filter((f) => (f.moduleCode ?? "") === selectedModCode),
    [features, selectedModCode]
  );
  const selectedApp = applications.find((a) => a.id === filterAppId) ?? null;
  const selectedModule = modules.find((m) => (m.code ?? "") === selectedModCode) ?? null;

  return (
    <div className="sm-page">
      <PageHeader
        title={t("application_features")}
        subtitle={t("application_features_sub")}
        actions={
          isAdmin ? (
            <button
              type="button"
              className="sm-btn sm-btn-primary"
              onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyForm); }}
            >
              + {t("new_feature")}
            </button>
          ) : null
        }
      />

      {error ? <div className="sm-alert sm-alert-error" role="alert">{error}</div> : null}

      {/* Layout A : volet gauche (applications / modules) + volet droit (features) */}
      <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 16, marginBottom: 16 }}>
        <div className="sm-card sm-card-pad">
          <h3 style={{ marginTop: 0, fontSize: 13 }}>{t("applications")}</h3>
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 16px" }}>
            <li>
              <button
                type="button"
                className="sm-btn"
                style={{
                  width: "100%",
                  textAlign: "left",
                  marginBottom: 6,
                  background: filterAppId === "" ? "rgba(31,78,121,0.1)" : undefined,
                }}
                onClick={() => setFilterAppId("")}
              >
                {t("all_applications")}
              </button>
            </li>
            {applications.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  className="sm-btn"
                  style={{
                    width: "100%",
                    textAlign: "left",
                    marginBottom: 6,
                    background: filterAppId === a.id ? "rgba(31,78,121,0.1)" : undefined,
                  }}
                  onClick={() => setFilterAppId(a.id)}
                >
                  {localeLabel(i18n.language, a)}
                </button>
              </li>
            ))}
          </ul>

          <h3 style={{ marginTop: 0, fontSize: 13 }}>Modules (N1)</h3>
          {modules.length === 0 ? (
            <p className="sm-muted" style={{ fontSize: 12 }}>{t("no_entries")}</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              <li>
                <button
                  type="button"
                  className="sm-btn"
                  style={{
                    width: "100%",
                    textAlign: "left",
                    marginBottom: 6,
                    background: selectedModCode === null ? "rgba(31,78,121,0.1)" : undefined,
                  }}
                  onClick={() => setSelectedModCode(null)}
                >
                  {t("all_modules")}
                  <span className="sm-muted" style={{ fontSize: 12 }}> ({features.length})</span>
                </button>
              </li>
              {modules.map((m) => (
                <li key={m.code ?? "__none__"}>
                  <button
                    type="button"
                    className="sm-btn"
                    style={{
                      width: "100%",
                      textAlign: "left",
                      marginBottom: 6,
                      background:
                        selectedModCode === (m.code ?? "") ? "rgba(31,78,121,0.1)" : undefined,
                    }}
                    onClick={() => setSelectedModCode(m.code ?? "")}
                  >
                    <strong>{m.code ?? t("no_module")}</strong>
                    <div className="sm-muted" style={{ fontSize: 12 }}>
                      {m.label ? `${m.label} (${m.count})` : `(${m.count})`}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="sm-card">
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 8,
              flexWrap: "wrap",
              padding: "12px 16px",
              borderBottom: "1px solid var(--sm-border)",
            }}
          >
            <strong>{selectedApp ? localeLabel(i18n.language, selectedApp) : t("all_applications")}</strong>
            {selectedModule ? (
              <span className="sm-muted">
                › {selectedModule.code ?? t("no_module")}
                {selectedModule.label ? ` — ${selectedModule.label}` : ""}
              </span>
            ) : (
              <span className="sm-muted">› {t("all_modules")}</span>
            )}
            <span className="sm-badge-pill sm-badge-blue" style={{ marginLeft: "auto" }}>
              {visibleFeatures.length}
            </span>
          </div>

          {loading ? (
            <p className="sm-muted" style={{ padding: 24, textAlign: "center" }}>{t("loading")}</p>
          ) : visibleFeatures.length === 0 ? (
            <p className="sm-muted" style={{ padding: 24, textAlign: "center" }}>{t("no_entries")}</p>
          ) : (
            <div className="sm-table-wrap">
              <table className="sm-table">
                <thead>
                  <tr>
                    <th>{t("feature_code")}</th>
                    <th>{t("label_en")}</th>
                    <th>{t("label_fr")}</th>
                    <th>Module (N1)</th>
                    <th>Sous-module (N2)</th>
                    <th>{t("description")}</th>
                    <th>{t("active")}</th>
                    <th>{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleFeatures.map((feat) => (
                    <tr key={feat.id} className={editingId === feat.id ? "sm-row-editing" : undefined}>
                      <td>
                        <code style={{ fontSize: "0.85em", fontWeight: 600 }}>{feat.code}</code>
                      </td>
                      <td>{feat.labelEn}</td>
                      <td>{feat.labelFr ?? "—"}</td>
                      <td>{feat.moduleCode ?? "—"}</td>
                      <td>{feat.subModuleCode ?? "—"}</td>
                      <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={feat.description ?? undefined}>
                        {feat.description ?? "—"}
                      </td>
                      <td>
                        <span className={`sm-badge-pill ${feat.active ? "sm-badge-green" : "sm-badge-red"}`}>
                          {feat.active ? t("status_active") : t("status_inactive")}
                        </span>
                      </td>
                      <td>
                        {isAdmin ? (
                          <div className="actions">
                            <button type="button" className="sm-link" onClick={() => startEdit(feat)}>{t("edit")}</button>
                            <button type="button" className="sm-link" onClick={() => void toggleActive(feat)}>
                              {feat.active ? t("deactivate") : t("activate")}
                            </button>
                          </div>
                        ) : (
                          <span className="sm-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Import — Admin only (référentiel global, cf. décision produit 2026-10-02) */}
      {isAdmin ? (
        <div className="sm-form-card" style={{ marginBottom: 16 }}>
          <h2>{t("import_excel_features")}</h2>
          <button type="button" className="sm-btn sm-btn-secondary" style={{ marginBottom: 8 }} onClick={() => void downloadTemplate()}>
            ⬇ {t("download_excel_template")}
          </button>
          <p className="sm-muted" style={{ marginBottom: 8, fontSize: "0.85em" }}>
            Colonnes : applicationLabel · moduleCode · moduleLabel · subModuleCode · subModuleLabel · <strong>code</strong> · labelEn · labelFr · description · active · sortOrder
          </p>
          <form onSubmit={onImport}>
            <div className="sm-form-actions">
              <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => setImportFile(e.target.files?.[0] ?? null)} disabled={busy} />
              <button type="submit" className="sm-btn sm-btn-primary" disabled={busy}>{t("import")}</button>
            </div>
          </form>
          {importReport ? (
            <div style={{ marginTop: 8 }}>
              <p className="sm-muted">
                {t("created_n")} : <strong>{importReport.created}</strong> —{" "}
                {t("updated_n")} : <strong>{importReport.updated}</strong> —{" "}
                {t("errors_n")} : <strong>{importReport.errors.length}</strong>
              </p>
              {importReport.errors.length > 0 ? (
                <ul className="sm-alert sm-alert-error" style={{ paddingLeft: 24 }}>
                  {importReport.errors.map((e) => (
                    <li key={`${e.row}-${e.message}`}>{t("excel_row", { row: e.row })} {e.message}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Create / edit form — Admin only */}
      {showForm && isAdmin ? (
        <form onSubmit={onSubmit} className="sm-form-card" style={{ maxWidth: 960 }}>
          <h2>{editingId ? t("edit_feature") : t("new_feature")}</h2>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
            {/* Application (create only) */}
            {!editingId ? (
              <div style={{ gridColumn: "1 / -1" }}>
                <label className="sm-muted" style={{ display: "block", marginBottom: 4 }}>{t("feature_application")} *</label>
                <select required className="sm-input" style={{ width: "100%", maxWidth: 320 }}
                  value={form.applicationId} onChange={(e) => setForm({ ...form, applicationId: e.target.value })}>
                  <option value="">{t("feature_application")}</option>
                  {applications.map((a) => (
                    <option key={a.id} value={a.id}>{localeLabel(i18n.language, a)}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div style={{ gridColumn: "1 / -1" }}>
                <span className="sm-badge-pill sm-badge-blue" style={{ fontSize: "0.9em" }}>
                  {features.find((f) => f.id === editingId)?.applicationLabel ?? ""}
                </span>
              </div>
            )}

            {/* N1 */}
            <div>
              <label className="sm-muted" style={{ display: "block", marginBottom: 4 }}>Code module (N1)</label>
              <input className="sm-input" style={{ width: "100%", textTransform: "uppercase" }}
                placeholder="ex: FI" value={form.moduleCode}
                onChange={(e) => setForm({ ...form, moduleCode: e.target.value.toUpperCase() })} />
            </div>
            <div style={{ gridColumn: "2 / -1" }}>
              <label className="sm-muted" style={{ display: "block", marginBottom: 4 }}>Module (N1)</label>
              <input className="sm-input" style={{ width: "100%" }}
                placeholder="ex: Financial Accounting" value={form.moduleLabel}
                onChange={(e) => setForm({ ...form, moduleLabel: e.target.value })} />
            </div>

            {/* N2 */}
            <div>
              <label className="sm-muted" style={{ display: "block", marginBottom: 4 }}>Code sous-module (N2)</label>
              <input className="sm-input" style={{ width: "100%", textTransform: "uppercase" }}
                placeholder="ex: FI-GL" value={form.subModuleCode}
                onChange={(e) => setForm({ ...form, subModuleCode: e.target.value.toUpperCase() })} />
            </div>
            <div style={{ gridColumn: "2 / -1" }}>
              <label className="sm-muted" style={{ display: "block", marginBottom: 4 }}>Sous-module (N2)</label>
              <input className="sm-input" style={{ width: "100%" }}
                placeholder="ex: General Ledger Accounting" value={form.subModuleLabel}
                onChange={(e) => setForm({ ...form, subModuleLabel: e.target.value })} />
            </div>

            {/* N3 */}
            <div>
              <label className="sm-muted" style={{ display: "block", marginBottom: 4 }}>Code N3 * {editingId ? "(non modifiable)" : ""}</label>
              <input required={!editingId} className="sm-input" style={{ width: "100%", textTransform: "uppercase" }}
                placeholder="ex: FI-GL-IS" value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                disabled={!!editingId} title={editingId ? t("code_immutable") : undefined} />
            </div>
            <div>
              <label className="sm-muted" style={{ display: "block", marginBottom: 4 }}>{t("label_en")} *</label>
              <input required className="sm-input" style={{ width: "100%" }}
                placeholder="ex: Information System" value={form.labelEn}
                onChange={(e) => setForm({ ...form, labelEn: e.target.value })} />
            </div>
            <div>
              <label className="sm-muted" style={{ display: "block", marginBottom: 4 }}>{t("label_fr")}</label>
              <input className="sm-input" style={{ width: "100%" }}
                placeholder="ex: Système d'information" value={form.labelFr}
                onChange={(e) => setForm({ ...form, labelFr: e.target.value })} />
            </div>

            {/* Description */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="sm-muted" style={{ display: "block", marginBottom: 4 }}>{t("description")}</label>
              <input className="sm-input" style={{ width: "100%" }}
                placeholder="ex: Reporting et analyses du grand livre" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>

            {/* sortOrder */}
            <div>
              <label className="sm-muted" style={{ display: "block", marginBottom: 4 }}>{t("sort_order")}</label>
              <input type="number" className="sm-input" min={0} value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} style={{ width: 100 }} />
            </div>
          </div>

          <div className="sm-form-actions">
            <button type="submit" className="sm-btn sm-btn-primary">{editingId ? t("save") : t("add")}</button>
            <button type="button" className="sm-btn sm-btn-ghost" onClick={cancelEdit}>{t("cancel")}</button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
